import { useLazyLoadQuery } from "react-relay"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { FilterValues } from "@/modules/care-provider/advanced-search/advanced-care-provider-search-dialog"
import AdvancedCareProviderSearchEmpty from "@/modules/care-provider/advanced-search/advanced-care-provider-search-empty"
import { AdvancedCareProviderSearchItem } from "@/modules/care-provider/advanced-search/advanced-care-provider-search-item"
import { AddressOption } from "@/modules/care-provider/advanced-search/care-provider-address-filter/care-provider-address-select"
import { useAdvancedCareProviderSearchFiltersQuery$data } from "@/modules/care-provider/advanced-search/services/__generated__/useAdvancedCareProviderSearchFiltersQuery.graphql"
import {
  GeoStructuredAddressInput,
  useAdvancedCareProviderSearchQuery,
  useAdvancedCareProviderSearchQuery$variables,
} from "@/modules/care-provider/advanced-search/services/__generated__/useAdvancedCareProviderSearchQuery.graphql"
import { advancedCareProviderSearchQuery } from "@/modules/care-provider/advanced-search/services/useAdvancedCareProviderSearchQuery"
import { FeatureId, FEATURES } from "@/modules/care-provider/advanced-search/types/AdvancedCareProviderFilter"
import { careProviderSearchDoctorNetworksQuery as careProviderSearchDoctorNetworksQueryType } from "@/modules/care-provider/simple-search/__generated__/careProviderSearchDoctorNetworksQuery.graphql"
import { careProviderSearchDoctorNetworksQuery } from "@/modules/care-provider/simple-search/care-provider-search"
import { type ApplicableInsuranceCareProviderListIds } from "@/modules/care-provider/simple-search/services/useCareProviderQuickListQuery"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

const CareProviderResult = ({
  filterValues,
  keyword,
  fetchKey,
  currentAddress,
  officialLanguages,
  selectedCareProviderIds,
  hasMPNReferral = false,
  applicableInsuranceCareProviderListIds,
  onCareProviderSelect,
}: {
  filterValues?: FilterValues
  keyword: string
  fetchKey: number
  currentAddress: AddressOption | null
  officialLanguages: useAdvancedCareProviderSearchFiltersQuery$data["officialLanguages"]
  hasMPNReferral?: boolean
  applicableInsuranceCareProviderListIds: ApplicableInsuranceCareProviderListIds
  selectedCareProviderIds: string[]
  onCareProviderSelect: (relationId: string) => void
}) => {
  const patientId = usePatientParam()

  const currentLanguage = useCurrentLanguage().toUpperCase()
  const { ch_patientById } = useLazyLoadQuery<careProviderSearchDoctorNetworksQueryType>(
    careProviderSearchDoctorNetworksQuery,
    {
      patientId,
    },
  )
  const doctorNetworks = ch_patientById?.doctorNetworks

  const {
    careProviderGroups,
    cantons,
    diagnosticFacilities,
    certificates,
    officialLanguages: filteredOfficialLanguages,
    foci,
    specializations,
    specialties,
    services,
    subjectAreas,
    features,
    isMedgatePartnerNetworkMember,
    isInsurerNetworkMember,
    isTakesNewPatients,
    isWalkIn,
  } = filterValues || {}

  const shouldRestrictToInsuranceCareProviderLists =
    !!isInsurerNetworkMember && applicableInsuranceCareProviderListIds.length > 0

  const careProviderGroupId = careProviderGroups?.id
  const cantonCode = cantons?.cantonCode

  const featureFilterFlags = Object.fromEntries(
    FEATURES.map((key) => [key, !!features?.find((item) => item.id === key)]),
  ) as Record<FeatureId, boolean>

  const address: GeoStructuredAddressInput | null = currentAddress
    ? {
        countryCode: currentAddress.countryCode,
        municipality: currentAddress.municipality,
        postalCode: currentAddress.postalCode,
        streetName: currentAddress.streetName,
      }
    : null

  const input: useAdvancedCareProviderSearchQuery$variables = {
    input: {
      careProviderGroupId: careProviderGroupId ? careProviderGroupId : undefined,
      canton: cantonCode ? cantonCode : undefined,
      currentAddress: address,
      diagnosticFacilitiesIds: diagnosticFacilities?.map((item) => item.id) ?? [],
      certificateIds: certificates?.map((item) => item.id) ?? [],
      officialLanguageIds: filteredOfficialLanguages?.map((item) => item.id) ?? [],
      focusId: foci?.id,
      specializationId: specializations?.id,
      serviceId: services?.id,
      specialtyId: specialties?.id,
      subjectAreaId: subjectAreas?.id,
      flagFilters: {
        isHandleAdultEmergency: featureFilterFlags.isHandleAdultEmergency,
        isHandleChildEmergency: featureFilterFlags.isHandleChildEmergency,
        isHomeTreatment: featureFilterFlags.isHomeTreatment,
        isTelemedicine: featureFilterFlags.isTelemedicine,
        isWheelchairAccessible: featureFilterFlags.isWheelchairAccessible,
        isMedgatePartnerNetworkMember: isMedgatePartnerNetworkMember ?? false,
        isTakesNewPatients: isTakesNewPatients ?? false,
        isWalkIn: isWalkIn ?? false,
        isPartnerPortalMember: false,
        isRecommendedForReferral: false,
      },
      insuranceCareProviderListIds: shouldRestrictToInsuranceCareProviderLists
        ? applicableInsuranceCareProviderListIds
        : undefined,
      searchText: keyword,
      searchType: "ADVANCED_SEARCH",
      top: 20,
    },
    language: currentLanguage,
  }

  const { ch_careProviderAdvancedSearch } = useLazyLoadQuery<useAdvancedCareProviderSearchQuery>(
    advancedCareProviderSearchQuery,
    input,
    {
      fetchPolicy: "store-and-network",
      fetchKey,
    },
  )

  const careProviderResults = ch_careProviderAdvancedSearch?.results ?? []
  const hasResults = careProviderResults.length > 0

  return (
    <div
      data-testid="care-provider-search-results"
      className="scrollbar-thumb-rounded-full scrollbar-w-1 hover:scrollbar-thumb-surface-pressed h-137.5 max-h-137.5 min-h-137.5 scrollbar-thin scrollbar-thumb-transparent scrollbar-track-transparent overflow-y-auto">
      {hasResults ? (
        <div className="grid grid-flow-row gap-4 pt-2 pr-5 pb-5 pl-5">
          {careProviderResults.map((item) => {
            return (
              <AdvancedCareProviderSearchItem
                key={item.id}
                careProvider={item}
                doctorNetworks={doctorNetworks}
                officialLanguages={officialLanguages}
                hasMPNReferral={hasMPNReferral}
                applicableInsuranceCareProviderListIds={applicableInsuranceCareProviderListIds}
                isAddable={!selectedCareProviderIds.includes(item.id)}
                onCareProviderSelect={onCareProviderSelect}
              />
            )
          })}
        </div>
      ) : (
        <AdvancedCareProviderSearchEmpty
          message="admin.careProviders.advancedSearch.content.emptyMessage"
          title="admin.careProviders.advancedSearch.content.emptyTitle"
        />
      )}
    </div>
  )
}

export { CareProviderResult }
